import React, { useState, useEffect, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { NavLink } from 'react-router-dom';
import classNames from "classnames";

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';

import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import ButtonBase from '@material-ui/core/ButtonBase';
import Divider from '@material-ui/core/Divider';
import FormGroup from '@material-ui/core/FormGroup'
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import CardHeader from '@material-ui/core/CardHeader';
import Avatar from '@material-ui/core/Avatar';
import Checkbox from '@material-ui/core/Checkbox';
import MenuItem from '@material-ui/core/MenuItem';

import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemIcon from '@material-ui/core/ListItemIcon';

import GroupIcon from '@material-ui/icons/Group';
import UserIcon from '@material-ui/icons/Person';
import CloseIcon from '@material-ui/icons/Close';
import TicketIcon from '@material-ui/icons/ConfirmationNumber';
import AddIcon from '@material-ui/icons/Add';

import Loading from 'app/components/loaders/Loading';
import SearchUsersAutoComplete from 'app/components/user/UserSearchAutocomplete';

import states from 'app/utils/States';

import useApi from 'app/context/useApi';

const useListItemStyles = makeStyles(theme => ({
    nested: props => ({
        paddingLeft: (10 + props.depth * 14),
    }),
    listItemIcon: {
        minWidth: 0
    }
}));

const findChildIds = (children) => {

    let ids = [];

    children.forEach(c => {

        ids.push(c.id);

        if (c.children_with_count.length > 0) {
            ids = ids.concat(findChildIds(c.children_with_count));
        }
    })

    return ids;
}

const NestedLevelItem = (props) => {

    const { level, depth, levelsChecked, onCheck, onCheckChildren } = props;

    const classes = useListItemStyles({ depth: depth});

    const [ includeChildren, setIncludeChildren ] = useState(false);

    const isSelected = levelsChecked.indexOf(level.id) > -1 ;

    const childIds = (level.children_with_count.length === 0) ? 0 : useMemo(() => findChildIds(level.children_with_count), []);

    const handleIncludeChildren = () => {
        setIncludeChildren(! includeChildren);
        const ids = [...childIds];
        if (! includeChildren) ids.push(level.id);
        onCheckChildren(! includeChildren, ids);
    }

    return (
        <>
            <ListItem
                classes={{ root: classes.nested }}
                button
                dense
                onClick={() => onCheck(level.id)}
                >
                <ListItemIcon classes={{ root: classes.listItemIcon }}>
                    <Checkbox
                        edge="start"
                        checked={isSelected}
                        tabIndex={-1}
                        disableRipple
                        color="primary"
                    />
                </ListItemIcon>
                <ListItemText
                    primary={ level.title }
                    className={ classes.listItemText }
                />
                <ListItemSecondaryAction>
                    <Typography
                        display="inline"
                        color="textSecondary"
                        variant="caption"
                        >
                        Incl. Sub
                    </Typography>
                    <Switch
                        edge="end"
                        onChange={handleIncludeChildren}
                        checked={includeChildren}
                        disabled={level.children_with_count.length === 0}
                        color="primary"
                    />
                </ListItemSecondaryAction>
            </ListItem>
            {level.children_with_count.map((child) => (
                <NestedLevelItem
                    key={child.id}
                    level={child}
                    depth={(depth + 1)}
                    levelsChecked={levelsChecked}
                    onCheck={onCheck}
                    onCheckChildren={onCheckChildren}
                />
            ))}
        </>
    )
}

const useStyles = makeStyles(theme => ({
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    container: {
        textAlign: 'center'
    },
    largeButtonContainer: {
        paddingTop: theme.spacing(4),
        paddingBottom: theme.spacing(4),
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        border: '2px solid #fff',
        borderRadius: theme.shape.borderRadius
    },
    largeButtonContainerSelected: {
        borderColor: theme.palette.primary.main
    },
    largeButtonIcon: {
        fontSize: '82px',
        color: theme.palette.text.secondary,
    },
    largeButtonText: {
        display: 'block'
    },
    marginBottom: {
        marginBottom: theme.spacing(3)
    },
    marginRight: {
        marginRight: theme.spacing(3)
    },
    subSectionLoading: {
        textAlign: 'center',
        paddingTop: theme.spacing(6),
        paddingBottom: theme.spacing(6),
    },
    dialogActions: {
        justifyContent: 'space-between'
    },
    groupSelect: {
        backgroundColor: theme.palette.grey[200],
        borderRadius: theme.shape.borderRadius,
        marginBottom: theme.spacing(3),
        maxHeight: 400,
        overflowY: 'scroll'
    },
    subSection: {
        padding: theme.spacing(1),
    },
    subSectionLoading: {
        textAlign: 'center',
        padding: theme.spacing(3),
    },
}));

const statusTypes = [
    {
        value: 'pending',
        label: 'Pending'
    },
    {
        value: 'maybe',
        label: 'Maybe'
    },
    {
        value: 'accepted',
        label: 'Accepted'
    },
    {
        value: 'declined',
        label: 'Declined'
    },
    {
        value: 'withdrawn',
        label: 'Withdrawn'
    },
]

const initialGuestValues = {
    email: "",
    phone_mobile: "",
    prefix: "",
    first: "",
    middle: "",
    last: "",
    suffix: "",
    address_one: "",
    address_two: "",
    city: "",
    state: "",
    zip: ""
}

export default function NewInvitationDialog(props) {

    const {
        open,
        tickets,
        event,
        getEvent,
        onClose,
        onSave,
    } = props;

    const classes = useStyles();
    const api = useApi();

    const [ step, setStep ] = useState(0);

    const [ type, setType ] = useState('group');
    const [ guestMethod, setGuestMethod ] = useState('existing');
    const [ user, setUser ] = useState(null);
    const [ guest, setGuest ] = useState(null);
    const [ ticket, setTicket ] = useState(null);
    const [ title, setTitle ] = useState((event?.invitation_title != '') ? event.invitation_title : "Your're Invited");
    const [ message, setMessage ] = useState('');
    const [ addSpouse, setAddSpouse ] = useState(true);
    const [ flag, setFlag ] = useState(false);
    const [ status, setStatus ] = useState('pending');
    const [ emailInvitation, setEmailInvitation ] = useState(false);

    const [ dateName, setDateName ] = useState("");
    const [ dates, setDates ] = useState([]);

    const [ guestValues, setGuestValues ] = useState(initialGuestValues);

    const [ groupsChecked, setGroupsChecked ] = useState([]);
    const [ groups, setGroups ] = useState([]);
    const [ levels, setLevels ] = useState([]);

    const [ loading, setLoading ] = useState(false);
    const [ errors, setErrors ] = useState({});

    const getGroups = () => api.get('/admin/group/group').then(res => setGroups(res.groups));

    const getLevels = () => api.get('/admin/group/level/recursive/0').then(res => setLevels(res.groups));

    useEffect(() => {
        getGroups();
        getLevels();
    },  []);

    useEffect(() => {
        if (ticket) setStatus('pending');
    }, [ticket]);

    useEffect(() => {
        if (type === 'group') setGuestMethod('existing');
    }, [type])

    // Setter Functions ------------------------------------------

    const handleNext = () => {
        switch (step) {
            case 0:
                if (type === 'guest') setStep(1);
                if (type === 'group') setStep(2);
                break;
            case 1:
            case 2:
                setStep(3);
                break;
            case 3:
                if (tickets && tickets.length > 0) setStep(4);
                else if (type === 'guest') setStep(5);
                else setStep(6);
                break;
            case 4:
                if (type === 'guest') setStep(5);
                else setStep(6);
                break;
            case 5:
                setStep(6);
                break;
            case 6:
                postInvitation();
                break;
        }
    }

    const handleBack = () => {
        switch (step) {
            case 1:
            case 2:
                setStep(0);
                break;
            case 3:
                if (type === 'guest') setStep(1);
                if (type === 'group') setStep(2);
                break;
            case 4:
                setStep(3);
                break;
            case 5:
                if (tickets && tickets.length > 0) setStep(4);
                else setStep(3);
                break;
            case 6:
                if (type === 'guest') setStep(5);
                else setStep(4);
                break;
        }
    }

    const isNextDisabled = () => {
        switch (step) {
            case 1:
                if (user && guestMethod === 'existing' && guest) return false;
                if (user && guestMethod === 'new' && guestValues.first != '' && guestValues.last != '') return false;
                return true;
            case 2:
                if (groupsChecked.length > 0) return false;
                return true;
            default: return false;
        }
    }

    const isBackDisabled = () => {
        if (step < 1) return true;
        return false
    }

    const getNextText = () => {
        if (step === 6) return (type === 'group') ? "Save Invitations" : "Save Invitation"
        return "Next";
    }

    const handleTitle = e => {
        const value = e.target.value;

        if (isError('title')) delete errors.title;

        setTitle(value);
    };

    const handleMessage = e => {
        const value = e.target.value;

        if (isError('message')) delete errors.message;

        setMessage(value);
    };

    const toggleTicket = t => {
        if (isError('ticket_id')) delete errors.ticket_id;

        if (isTicketChecked(t)) setTicket(null);
        else setTicket(t);
    };

    const handleUser = u => {
        if (isError('user_id')) delete errors.user_id;
        setUser(u);
    };

    const handleGuest = g => {
        if (isError('guest_id')) delete errors.guest_id;
        setGuest(g);
    };

    const handleAddSpouse = e => {
        const value = e.target.checked;

        if (isError('addSpouse')) delete errors.addSpouse;

        setAddSpouse(value);
    };

    const handleStatus = e => {
        const value = e.target.value;

        if (isError('status')) delete errors.status;

        setStatus(value);
    };

    const handleFlag = e => {
        const value = e.target.checked;

        setFlag(value);
    };

    const toggleTypeGuest = () => setType('guest');
    const toggleTypeGroup = () => setType('group');

    const toggleGuestMethodNew = () => setGuestMethod('new');
    const toggleGuestMethodExisting = () => setGuestMethod('existing');

    const handleGuestChange = name => e => {

        const target = e.target;
        const value = (target.type === 'checkbox' ? target.checked : target.value);

        if (isError(name)) delete errors[name];

        setGuestValues({ ...guestValues, [name]: value });
    };

    const handleDateName = e => {
        const value = e.target.value;

        setDateName(value);
    };

    const addDate = () => {
        if (dateName != '') {
            setDates([...dates, {name: dateName}]);
            setDateName('');
        }
    }

    const removeDate = i => {
        const newDates = [...dates];

        if (newDates[i]) newDates.splice(i,1);

        setDates(newDates);
    }

    const handleEmailInvitation = e => {
        const value = e.target.checked;
        setEmailInvitation(value);
    }

    const handleGroup = id => {
        const newChecked = [...groupsChecked]
        const i = newChecked.indexOf(id)

        if (i > -1) {
            newChecked.splice(i, 1)
        } else {
            newChecked.push(id)
        }

        setGroupsChecked(newChecked);
    }

    const handleLevelChildren = (addChildren, ids) => {
        let newChecked = [...groupsChecked]

        if (addChildren) {
            newChecked = [...new Set(newChecked.concat(ids))];
        } else {
            newChecked = newChecked.filter((el) => !ids.includes(el));
        }

        setGroupsChecked(newChecked);
    }

    // Post functions ------------------------------------------

    const postInvitation = () => {

        setLoading(true);

        const data = {
            type: type,
            guestMethod: guestMethod,
            groups: groupsChecked,
            ticket_id: ticket ? ticket.id : null,
            user_id: user ? user.id : null,
            guest_id: guest ? guest.id : null,
            title: title,
            message: message,
            addSpouse: addSpouse,
            status: status,
            flag: flag,
            event_id: event.id,
            guestValues: {...guestValues},
            dates: dates,
            emailInvitation: emailInvitation
        }

        api.post(`/admin/invitation`, data)
            .then(() => {
                onSave();
                getEvent();
            })
            .catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            }).finally(() => setLoading(false));
    }

    const isError = field => {
        if (errors[field]) return true;
        return false;
    }

    const getErrorMessage = field => {
        if (isError(field)) return errors[field];
        return "";
    }

    const getMessageText = () => {
        if (isError('message')) return errors.message;
        return "Leave blank if you wish to use a default message."
    }

    const getStatusText = () => {
        if (isError('status')) return errors.message;
        else if (ticket !== null) return "Can not set the status of an invitation with a ticket attached."
        return "";
    }

    const getLargeButtonClassNames = (option) => {
        return classNames({
            [classes.largeButtonContainer]: true,
            [classes.largeButtonContainerSelected]: (option === type),
        });
    };

    const handleClosed = () => {
        setStep(0);
        setType('group');
        setGuestMethod('existing');
        setGroupsChecked([]);
        setUser(null);
        setGuest(null);
        setTicket(null);
        setTitle(event.invitation_title ?? "Your're Invited");
        setMessage('');
        setAddSpouse(true);
        setFlag(false);
        setStatus('pending');
        setDateName('');
        setDates([]);
        setLoading(false);
        setGuestValues(initialGuestValues);
    }

    const initials = user => user.first.charAt(0) + user.last.charAt(0);

    const isTicketChecked = t => {
        if (ticket && ticket.id === t.id) return true;
        return false;
    }

    return (
        <Dialog
            aria-labelledby="new-invitation-dialog-title"
            open={open}
            onClose={onClose}
            aria-describedby="new-invitation-dialogue-description"
            fullWidth
            maxWidth="sm"
            disableBackdropClick
            TransitionProps={{
                onExited: handleClosed
            }}
            >
            <Loading isLoading={loading}/>
            <DialogTitle id="new-invitation-dialog-title">
                <span>New Invitation</span>
                <IconButton aria-label="Cancel" className={classes.closeButton} onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {(step === 0) &&
                    <Grid container spacing={3} wrap='nowrap'>
                        <Grid item sm={6} className={classes.container}>
                            <ButtonBase
                                focusRipple
                                className={getLargeButtonClassNames('guest')}
                                onClick={toggleTypeGuest}
                                >
                                <UserIcon className={classes.largeButtonIcon}/>
                                <Typography className={classes.largeButtonText} color="textSecondary" variant="h6">Invite a Guest</Typography>
                            </ButtonBase>
                        </Grid>
                        <Grid item xs='auto'>
                            <Divider orientation="vertical"/>
                        </Grid>
                        <Grid item sm={6} className={classes.container}>
                            <ButtonBase
                                focusRipple
                                className={getLargeButtonClassNames('group')}
                                onClick={toggleTypeGroup}
                                >
                                <GroupIcon className={classes.largeButtonIcon}/>
                                <Typography className={classes.largeButtonText} color="textSecondary" variant="h6">Invite a Group</Typography>
                            </ButtonBase>
                        </Grid>
                    </Grid>
                }
                {(step === 1) &&
                    <>
                        <Typography variant="subtitle1" className={classes.marginBottom}>Inviter/Invitee Details</Typography>
                        <Grid container spacing={3}>
                            <Grid item>
                                <Typography variant="subtitle1" color="textSecondary">
                                    Select the Inviter
                                </Typography>
                            </Grid>
                        </Grid>
                        <div className={classes.marginBottom}>
                            {user ? (
                                <CardHeader
                                    avatar={
                                        <Avatar
                                            aria-label={user.full_name}
                                            alt={user.full_name}
                                            src={user.avatar ? user.avatar.full_url : null}
                                            >
                                            {initials(user)}
                                        </Avatar>
                                    }
                                    title={user.full_name}
                                    subheader={user.membership_title}
                                    action={
                                        <IconButton
                                            aria-label="Remove Inviter"
                                            onClick={() => handleUser(null)}
                                            >
                                            <CloseIcon />
                                        </IconButton>
                                    }
                                />
                            ) : (
                                <SearchUsersAutoComplete
                                    closeOnSelect
                                    setOnSelect
                                    onSelect={handleUser}
                                    label="Search Users"
                                />
                            )}
                        </div>
                        {(guestMethod === 'existing') &&
                            <div className={classes.marginBottom}>
                                <Grid container spacing={3} justify='space-between' alignItems='center'>
                                    <Grid item>
                                        <Typography variant="subtitle1" color="textSecondary">
                                            Select the Invitee (Guest)
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Button onClick={toggleGuestMethodNew} color="primary">
                                            Or add new guest
                                        </Button>
                                    </Grid>
                                </Grid>
                                {guest ? (
                                    <CardHeader
                                        avatar={
                                            <Avatar
                                                aria-label={guest.full_name}
                                                alt={guest.full_name}
                                                src={guest.avatar ? guest.avatar.full_url : null}
                                                >
                                                {initials(guest)}
                                            </Avatar>
                                        }
                                        title={guest.full_name}
                                        subheader={guest.membership_title}
                                        action={
                                            <IconButton
                                                aria-label="Remove Invitee"
                                                onClick={() => handleGuest(null)}
                                                >
                                                <CloseIcon />
                                            </IconButton>
                                        }
                                    />
                                ) : (
                                    <SearchUsersAutoComplete
                                        closeOnSelect
                                        setOnSelect
                                        onSelect={handleGuest}
                                        label="Search Users"
                                    />
                                )}
                            </div>
                        }
                        {(guestMethod === 'new') &&
                            <div className={classes.marginBottom}>
                                <Grid container spacing={3} justify='space-between' alignItems='center'>
                                    <Grid item>
                                        <Typography variant="subtitle1" color="textSecondary">
                                            Create a New Guest
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Button onClick={toggleGuestMethodExisting} color="primary">
                                            Or find existing guest
                                        </Button>
                                    </Grid>
                                </Grid>
                                <TextField
                                    id="input-prefix"
                                    label="Prefix"
                                    value={guestValues.prefix}
                                    onChange={handleGuestChange('prefix')}
                                    fullWidth
                                    error={isError('prefix')}
                                    helperText={getErrorMessage('prefix')}
                                    variant='outlined'
                                    margin="normal"
                                />
                                <TextField
                                    id="input-first"
                                    label="First"
                                    value={guestValues.first}
                                    onChange={handleGuestChange('first')}
                                    fullWidth
                                    required
                                    error={isError('first')}
                                    helperText={getErrorMessage('first')}
                                    margin="normal"
                                    variant="outlined"
                                />
                                <TextField
                                    id="input-middle"
                                    label="Middle"
                                    value={guestValues.middle}
                                    onChange={handleGuestChange('middle')}
                                    fullWidth
                                    error={isError('middle')}
                                    helperText={getErrorMessage('middle')}
                                    margin="normal"
                                    variant="outlined"
                                />
                                <TextField
                                    id="input-last"
                                    label="Last"
                                    value={guestValues.last}
                                    onChange={handleGuestChange('last')}
                                    fullWidth
                                    required
                                    error={isError('last')}
                                    helperText={getErrorMessage('last')}
                                    margin="normal"
                                    variant="outlined"
                                />
                                <TextField
                                    id="input-suffix"
                                    label="Suffix"
                                    value={guestValues.suffix}
                                    onChange={handleGuestChange('suffix')}
                                    fullWidth
                                    margin="normal"
                                    variant="outlined"
                                    error={isError('suffix')}
                                    helperText={getErrorMessage('suffix')}
                                    className={classes.marginBottom}
                                />
                                <Typography variant="body1" color="textSecondary">
                                    Guest Address
                                </Typography>
                                <TextField
                                    id="input-address-one"
                                    label="Address One"
                                    value={guestValues.address_one}
                                    onChange={handleGuestChange('address_one')}
                                    fullWidth
                                    error={isError('address_one')}
                                    helperText={getErrorMessage('address_one')}
                                    margin="normal"
                                    variant="outlined"
                                />
                                <TextField
                                    id="input-address-two"
                                    label="Address Two"
                                    value={guestValues.address_two}
                                    onChange={handleGuestChange('address_two')}
                                    error={isError('address_two')}
                                    helperText={getErrorMessage('address_two')}
                                    fullWidth
                                    margin="normal"
                                    variant="outlined"
                                />
                                <TextField
                                    id="input-city"
                                    label="City"
                                    value={guestValues.city}
                                    onChange={handleGuestChange('city')}
                                    fullWidth
                                    margin="normal"
                                    variant="outlined"
                                    error={isError('city')}
                                    helperText={getErrorMessage('city')}
                                />
                                <Grid container spacing={3} className={classes.marginBottom}>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            id="input-state"
                                            select
                                            label="State"
                                            value={guestValues.state}
                                            onChange={handleGuestChange('state')}
                                            fullWidth
                                            margin="normal"
                                            variant="outlined"
                                            error={isError('state')}
                                            helperText={getErrorMessage('state')}
                                            >
                                            {states.map( state => {
                                                return (
                                                    <MenuItem key={state.abbreviation} value={state.abbreviation} >{state.name}</MenuItem>
                                                )
                                            })}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={8}>
                                        <TextField
                                            id="input-zip"
                                            label="ZIP"
                                            value={guestValues.zip}
                                            onChange={handleGuestChange('zip')}
                                            fullWidth
                                            margin="normal"
                                            variant="outlined"
                                            error={isError('zip')}
                                            helperText={getErrorMessage('zip')}
                                        />
                                    </Grid>
                                </Grid>
                                <Typography variant="body1" color="textSecondary">
                                    Guest Contact
                                </Typography>
                                <TextField
                                    id="input-email"
                                    label="Email"
                                    type="email"
                                    autoComplete="email"
                                    value={guestValues.email}
                                    onChange={handleGuestChange('email')}
                                    fullWidth
                                    margin="normal"
                                    error={isError('email')}
                                    helperText={getErrorMessage('email')}
                                    variant="outlined"
                                />
                                <TextField
                                    id="input-mobile"
                                    label="Mobile Phone"
                                    value={guestValues.phone_mobile}
                                    onChange={handleGuestChange('phone_mobile')}
                                    fullWidth
                                    margin="normal"
                                    error={isError('phone_mobile')}
                                    helperText={getErrorMessage('phone_mobile')}
                                    variant="outlined"
                                />
                            </div>
                        }
                    </>
                }
                {(step === 2) &&
                    <>
                        <Typography variant="subtitle1" gutterBottom>Select the Groups you would like to send invitations to.</Typography>
                        <div className={classes.groupSelect}>
                            {(groups.length > 0) && (
                                <FormGroup className={classes.subSection}>
                                    {groups.map(group => (
                                        <FormControlLabel
                                            className={classes.noMarginLeft}
                                            key={group.id}
                                            control={
                                                <Checkbox
                                                    checked={(groupsChecked.indexOf(group.id) > -1)}
                                                    onChange={() => handleGroup(group.id)}
                                                    name={group.title}
                                                    color="primary"
                                                    size="small"
                                                />
                                            }
                                            label={group.title}
                                        />
                                    ))}
                                </FormGroup>
                            )}
                            {(levels.length > 0) && (
                                <List className={classes.noContentPadding}>
                                    {levels.map(level => (
                                        <NestedLevelItem
                                            key={level.id}
                                            level={level}
                                            depth={1}
                                            levelsChecked={groupsChecked}
                                            onCheck={handleGroup}
                                            onCheckChildren={handleLevelChildren}
                                        />
                                    ))}
                                </List>
                            )}
                            {(groups.length == 0 && levels.length == 0) && (
                                <div className={classes.subSection}>
                                    <Typography
                                        variant="subtitle2"
                                        color="textSecondary"
                                        className={classes.empty}
                                        align="center"
                                        >
                                        No Available Groups
                                    </Typography>
                                </div>
                            )}
                        </div>
                    </>
                }
                {(step === 3) &&
                    <>
                        <Typography variant="subtitle1" gutterBottom>Invitation Content</Typography>
                        <TextField
                            id="invitation-title"
                            label="Invitation Title"
                            value={title}
                            onChange={handleTitle}
                            variant="outlined"
                            fullWidth
                            placeholder=""
                            error={isError('title')}
                            helperText={getErrorMessage('title')}
                            margin="normal"
                        />
                        <TextField
                            id="invitation-message"
                            value={message}
                            label='Invitation Message'
                            onChange={handleMessage}
                            variant="outlined"
                            fullWidth
                            multiline
                            rows={4}
                            error={isError('message')}
                            helperText={getMessageText()}
                            margin="normal"
                        />
                    </>
                }
                {(step === 4) &&
                    <>
                        <Typography variant="subtitle1" gutterBottom>Add a Ticket to the Invitation</Typography>
                        {tickets.map(t => (
                            <Paper key={t.id} elevation={4} className={classes.marginBottom}>
                                <ListItem ContainerComponent="div">
                                    <ListItemAvatar>
                                        <Avatar>
                                            <TicketIcon />
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={
                                            <>
                                                <Typography
                                                    component="span"
                                                    variant="subtitle2"
                                                    color="textPrimary"
                                                    >
                                                    {t.title}&nbsp;-&nbsp;
                                                </Typography>
                                                <Typography
                                                    component="span"
                                                    variant="subtitle2"
                                                    color="textSecondary"
                                                    >
                                                    {t.description}
                                                </Typography>
                                            </>
                                        }
                                        secondary={
                                            <Typography
                                                component="div"
                                                variant="body2"
                                                color="textSecondary"
                                                >
                                                Price: <strong>${t.current_price}</strong>
                                            </Typography>
                                        }/>
                                    <ListItemSecondaryAction>
                                        <Checkbox color="primary" checked={isTicketChecked(t)} onChange={() => toggleTicket(t)} />
                                    </ListItemSecondaryAction>
                                </ListItem>
                            </Paper>
                        ))}
                    </>
                }

                {(step === 5) &&
                    <>
                        <Typography variant="subtitle1" gutterBottom>Add dates of the guest to the invitation.</Typography>
                        {(dates.length > 0) && dates.map((d, i) => (
                            <Paper key={i} elevation={4} className={classes.marginBottom}>
                                <CardHeader
                                    avatar={
                                        <Avatar >
                                            <UserIcon />
                                        </Avatar>
                                    }
                                    title={d.name}
                                    action={
                                        <IconButton
                                            aria-label="Remove Date"
                                            onClick={() => removeDate(i)}
                                            >
                                            <CloseIcon />
                                        </IconButton>
                                    }
                                    className={classes.marginBottom}
                                />
                            </Paper>

                        ))}
                        <CardHeader
                            avatar={
                                <Avatar >
                                    <UserIcon />
                                </Avatar>
                            }
                            title={
                                <>
                                    <TextField
                                        id="date-name"
                                        label="Date Name"
                                        value={dateName}
                                        onChange={handleDateName}
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                    />
                                </>
                            }
                            subtitle="Enter date name and press the add button on the right to save."
                            action={
                                <IconButton
                                    aria-label="Remove Inviter"
                                    onClick={addDate}
                                    >
                                    <AddIcon />
                                </IconButton>
                            }
                            className={classes.marginBottom}
                        />
                        <Typography variant="subtitle1" color="primary" gutterBottom>
                            <strong>{dates.length}</strong> dates added to invitation.
                        </Typography>
                    </>
                }
                {(step === 6) &&
                    <>
                        <Typography variant="subtitle2" gutterBottom>Set the status of the invitations. Typically this will be 'Pending'.</Typography>
                        <TextField
                            id="select-status"
                            select
                            label="Invitation Status"
                            value={status}
                            onChange={handleStatus}
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            error={isError('status')}
                            helperText={getStatusText('status')}
                            className={classes.marginBottom}
                            disabled={(ticket !== null)}
                            >
                            {statusTypes.map((s) => (
                                <MenuItem key={s.value} value={s.value}>
                                    {s.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        {(type === 'group') &&
                            <>
                                <Typography variant="subtitle2" gutterBottom>If a user has added a spouse to their account, we can automatically add them to the invitation.</Typography>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={addSpouse}
                                            onChange={handleAddSpouse}
                                            color="primary"
                                            name="add_spouse"
                                        />
                                    }
                                    label="Would you like to add a user's spouse to the invitation as their date if possible?"
                                    labelPlacement="end"
                                    margin="normal"
                                    className={classes.marginBottom}
                                />
                            </>
                        }
                        <Typography variant="subtitle2" gutterBottom>Invitations can be flagged to let event administrators know that it needs to be reviewed.</Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={flag}
                                    onChange={handleFlag}
                                    color="primary"
                                    name="flag"
                                />
                            }
                            label={`Would you like to flag ${(type === 'guest') ? 'this invitation' : 'invitations'} for review?`}
                            labelPlacement="end"
                            margin="normal"
                            className={classes.marginBottom}
                        />
                        <Typography variant="subtitle2" gutterBottom>Invitations can emailed to users, with new guest accounts receiving a link to complete their account.</Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={emailInvitation}
                                    onChange={handleEmailInvitation}
                                    color="primary"
                                    name="flag"
                                />
                            }
                            label={`Would you like to email ${(type === 'guest') ? 'this invitation' : 'invitations'} to ${(type === 'guest') ? 'the user' : 'users'}?`}
                            labelPlacement="end"
                            margin="normal"
                        />
                    </>
                }
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
                <Button
                    to={`/admin/events/${event.id}/bulk-upload`}
                    variant='outlined'
                    component={NavLink}
                    color="primary"
                    >
                    Bulk Upload
                </Button>
                <div>
                    <Button onClick={handleBack} disabled={isBackDisabled()}>
                        Back
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        disabled={isNextDisabled()}
                        onClick={handleNext}
                        >
                        {getNextText()}
                    </Button>
                </div>
            </DialogActions>
        </Dialog>
    );
}
